// @flow
import get from 'lodash/fp/get';
import flatten from 'lodash/fp/flatten';
import intersection from 'lodash/fp/intersection';

type WhereArrayValue = $ReadOnlyArray<string>;

// eg. { "$user.type": "rep" }
// or  { "$customer.show_order_type": true }
type Condition = {
  +[string]: string | boolean | null | WhereArrayValue,
};

export type Where = $ReadOnlyArray<Condition>;

export type ObjectQueryMatcher = $ReadOnly<{|
  where?: Where,
|}>;

type Matcher = ($ReadOnly<{ ...ObjectQueryMatcher }>) => boolean;

// Example: 'user' becomes /^\$user\./
export const targetMatcher = (targetName: string): RegExp =>
  new RegExp(`^\\$${targetName}\\.`);

export const pathWithoutTarget = (path: string, targetName: string): string =>
  path.replace(targetMatcher(targetName), '');

export const valueMatches = (matcher: any, value: any): boolean =>
  (matcher ||
    matcher === value ||
    (typeof matcher === 'boolean' && matcher.toString() === value)) &&
  ((Array.isArray(value) &&
    Array.isArray(matcher) &&
    intersection(matcher)(value).length > 0) ||
    (Array.isArray(value) &&
      !Array.isArray(matcher) &&
      value.includes(matcher)) ||
    (Array.isArray(matcher) && matcher.includes(value)) ||
    // Number(matcher) === Number(value) is actually insane - thanks Javascript
    matcher === value ||
    (typeof matcher === 'boolean' && matcher.toString() === value) ||
    (!Array.isArray(matcher) && Number(matcher) === Number(value)) ||
    !!matcher[value]);

export const matchesAtPath = (object: any, path: string, value: any): boolean =>
  valueMatches(get(path, object), value);

export const conditionMatcherFor =
  (object: $ReadOnly<Object>, name: string): Matcher =>
  ({ where = [] }) =>
    flatten((where: Where).map((o) => Object.entries(o)))
      .filter(([path]) => targetMatcher(name).test(path))
      .map(([path, value]) => [pathWithoutTarget(path, name), value])
      .every(([path, value]) => matchesAtPath(object, path, value));
